import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { money } from '../lib/format'
import { Panel, Alert, Loading, Empty, Tag } from '../components/ui'

interface TodoData {
  today: string
  work_orders: Array<{
    id: number
    code: string
    period: string
    status: string
    writer_name: string | null
    owner_name: string | null
    due_on: string | null
    days_left: number | null
    total_qty: number
    total_amount: number
  }>
  billings: Array<{
    acceptance_id: number
    acceptance_code: string
    work_order_code: string | null
    writer_name: string | null
    period: string
    conclusion: string
    qty: number
    amount: number
  }>
  contracts: Array<{
    id: number
    name: string
    name_en: string | null
    lang: string
    status: string
    expires_on: string
    days_left: number
  }>
  badge: number
  /** 只有 ?mine=1 時才有：登入帳號對不到人員主檔 */
  mine?: boolean
  unmatched?: boolean
}

const STATUSES = ['待補充', '待審核', '待確認', '待交稿', '已交稿', '驗收中']

/**
 * 每個狀態接下來該做什麼，以及點下去要去哪
 *
 * 收整張單而不只收 id：驗收單是用「發包單號」帶出明細的（prefill/:code），
 * 這裡把單號一起帶進網址，使用者才不用回頭抄一次。
 */
const NEXT_STEP: Record<string, { label: string; to: (o: { id: number; code: string }) => string }> = {
  待補充: { label: '補完送出', to: (o) => `/work-orders/${o.id}` },
  待審核: { label: '前往審核', to: () => '/reviews' },
  待確認: { label: '確認接單', to: (o) => `/work-orders/${o.id}` },
  待交稿: { label: '催稿', to: (o) => `/work-orders/${o.id}` },
  已交稿: { label: '建立驗收單', to: (o) => `/acceptances/new?wo=${encodeURIComponent(o.code)}` },
  // 「驗收中」代表還有沒驗完的篇數，下一步本來就是建下一批，直接進表單省一步
  驗收中: { label: '建立下一批驗收單', to: (o) => `/acceptances/new?wo=${encodeURIComponent(o.code)}` },
}

/** 剩餘天數：負數是逾期，這裡順便決定要不要標紅 */
function dueText(days: number | null, status: string) {
  if (status === '已交稿' || status === '驗收中') return { text: '已交稿', color: undefined }
  if (days === null) return { text: '—', color: undefined }
  if (days < 0) return { text: `逾期 ${-days} 天`, color: 'var(--bad)' }
  if (days <= 3) return { text: `${days} 天`, color: 'var(--warn)' }
  return { text: `${days} 天`, color: undefined }
}

/** mine = 只看自己負責的案件（選單的「我的案件」），其餘畫面完全共用 */
export default function Todos({ mine }: { mine?: boolean } = {}) {
  const [data, setData] = useState<TodoData | null>(null)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('全部')

  useEffect(() => {
    setData(null)
    setFilter('全部')
    api.get<TodoData>(mine ? '/todos?mine=1' : '/todos').then(setData).catch((e) => setError(e.message))
  }, [mine])

  const orders = data?.work_orders ?? []
  const shown = filter === '全部' ? orders : orders.filter((o) => o.status === filter)
  const overdue = orders.filter((o) => o.days_left !== null && o.days_left < 0
    && o.status !== '已交稿' && o.status !== '驗收中')
  const billingAmount = (data?.billings ?? []).reduce((s, b) => s + b.amount, 0)

  return (
    <>
      <div className="page-head">
        <h2>{mine ? '我的案件' : '待辦事項'}</h2>
        {data && (
          <span className="hint">
            {mine
              ? `我負責的進行中發包單 ${data.work_orders.length} 張 · 資料時間 ${data.today}`
              : `共 ${data.badge} 件待處理 · 資料時間 ${data.today}`}
          </span>
        )}
      </div>

      <Alert kind="error">{error}</Alert>

      {/* 對不到人員主檔就講清楚，不要讓人以為自己真的沒案子 */}
      {data?.unmatched && (
        <Alert kind="warn">
          你的登入帳號還沒對應到人員主檔，因此看不到任何案件。
          請管理員到「主檔維護 → 人員」把你的 Email 填上，就會自動對應。
        </Alert>
      )}

      {!data ? (
        <Loading />
      ) : (
        <>
          <div className="stat-row" style={{ marginBottom: 16 }}>
            <div className="stat">
              <div className="label">進行中發包單</div>
              <div className="value">{orders.length}<span className="unit">張</span></div>
            </div>
            <div className="stat">
              <div className="label">其中逾期</div>
              <div className="value" style={overdue.length > 0 ? { color: 'var(--bad)' } : undefined}>
                {overdue.length}<span className="unit">張</span>
              </div>
            </div>
            <div className="stat">
              <div className="label">待請款驗收單</div>
              <div className="value">
                {data.billings.length}
                <span className="unit">張 / {money(billingAmount)} 元</span>
              </div>
            </div>
            <div className="stat">
              <div className="label">合約 90 天內到期</div>
              <div className="value">{data.contracts.length}<span className="unit">位</span></div>
            </div>
          </div>

          <Panel
            title={`進行中發包單（${orders.length} 張）`}
            actions={
              <span className="chip-row">
                {['全部', ...STATUSES].map((s) => {
                  const n = s === '全部' ? orders.length : orders.filter((o) => o.status === s).length
                  return (
                    <button
                      key={s}
                      className={`chip${filter === s ? ' on' : ''}`}
                      onClick={() => setFilter(s)}
                    >
                      {s} {n}
                    </button>
                  )
                })}
              </span>
            }
          >
            {shown.length === 0 ? (
              <Empty>沒有進行中的發包單</Empty>
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>發包單號</th>
                      <th>狀態</th>
                      <th>負責人</th>
                      <th>寫手</th>
                      <th>交件日</th>
                      <th className="num">剩餘</th>
                      <th className="num">篇數</th>
                      <th className="num">金額</th>
                      <th>下一步</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((o) => {
                      const due = dueText(o.days_left, o.status)
                      const step = NEXT_STEP[o.status]
                      return (
                        <tr key={o.id}>
                          <td>
                            <Link to={`/work-orders/${o.id}`}>{o.code}</Link>
                          </td>
                          <td><Tag value={o.status} /></td>
                          <td>{o.owner_name ?? '—'}</td>
                          <td>{o.writer_name ?? '—'}</td>
                          <td>{o.due_on ?? '—'}</td>
                          <td className="num" style={due.color ? { color: due.color } : undefined}>
                            {due.text}
                          </td>
                          <td className="num">{o.total_qty}</td>
                          <td className="num">{money(o.total_amount)}</td>
                          <td>
                            <span className="link-actions">
                              <Link to={step ? step.to(o) : `/work-orders/${o.id}`}>
                                {step ? step.label : '檢視'}
                              </Link>
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="hint" style={{ marginBottom: 0, fontSize: 12, color: 'var(--ink-soft)' }}>
              逾期的排在最前面，其餘依交件日由近到遠。
            </p>
          </Panel>

          <Panel title={`待請款驗收單（${data.billings.length} 張 · ${money(billingAmount)} 元）`}>
            {data.billings.length === 0 ? (
              <Empty>沒有待請款的驗收單</Empty>
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>驗收單號</th>
                      <th>發包單號</th>
                      <th>寫手</th>
                      <th>結論</th>
                      <th className="num">篇數</th>
                      <th className="num">金額</th>
                      <th>下一步</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.billings.map((b) => (
                      <tr key={b.acceptance_id}>
                        <td>{b.acceptance_code}</td>
                        <td>{b.work_order_code ?? '—'}</td>
                        <td>{b.writer_name ?? '—'}</td>
                        <td><Tag value={b.conclusion} /></td>
                        <td className="num">{b.qty}</td>
                        <td className="num">{money(b.amount)}</td>
                        <td>
                          <span className="link-actions">
                            <Link to="/billings">前往請款</Link>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title={`90 天內到期的寫手合約（${data.contracts.length} 位）`}>
            {data.contracts.length === 0 ? (
              <Empty>近期沒有合約到期</Empty>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th>寫手</th>
                    <th>語系</th>
                    <th>合作狀態</th>
                    <th>到期日</th>
                    <th className="num">剩餘天數</th>
                    <th>下一步</th>
                  </tr>
                </thead>
                <tbody>
                  {data.contracts.map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.name} {c.name_en && <span style={{ color: 'var(--ink-soft)' }}>{c.name_en}</span>}
                      </td>
                      <td>{c.lang}</td>
                      <td><Tag value={c.status} /></td>
                      <td>{c.expires_on}</td>
                      <td
                        className="num"
                        style={c.days_left <= 30
                          ? { color: 'var(--bad)' }
                          : c.days_left <= 60 ? { color: 'var(--warn)' } : undefined}
                      >
                        {c.days_left < 0 ? `已過期 ${-c.days_left} 天` : `${c.days_left} 天`}
                      </td>
                      <td>
                        <span className="link-actions">
                          <Link to="/masters">前往主檔</Link>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Panel>
        </>
      )}
    </>
  )
}
